import { useMutation } from '@apollo/client';
import React, { useContext, useMemo, useState } from 'react';
import { MODALS } from '../constants';
import { PlanningContext } from '../context';
import { UPDATE_ORDERS_MANY } from '../graphql/mutations';
import { QUERY_PLANNING_DATA } from '../graphql/queries';
import { matchesOrderCondition } from '../graphql/planningBuckets';
import { Body2, GridItemRow, ModalActions, ModalContent, ModalTitle, PrimaryButton, SecondaryButton } from '../blocks';
import NavResponsiveModal from '@/components/Navigation/NavResponsiveModal';
import { TextField } from '@material-ui/core';
import { asUTCDate } from '@/utilities/convertToISO';
import RescheduleReasonFields from '@/components/Reschedule/RescheduleReasonFields';
import useRescheduleReason from '@/components/Reschedule/useRescheduleReason';

const OrdersReschedule = () => {
    const {
        modalOpen,
        setModalOpen,
        setError,
        setNotification,
        selectedOrders,
        setSelectedOrders,
        deliveryDate,
        state: { orders, variables },
    } = useContext(PlanningContext);

    const [rescheduleDate, setRescheduleDate] = useState(deliveryDate);
    const { patch: reschedulePatch, isValid, fieldProps } = useRescheduleReason();

    const [updateOrders, { loading }] = useMutation(UPDATE_ORDERS_MANY);

    const onClose = () => {
        setModalOpen(null);
    };

    const ordersToReschedule = useMemo(() => {
        const ordersByKey = Object.fromEntries(
            orders.map((order) => [`${order.order_id}_${order.crossdock_leg}`, order])
        );
        return Object.entries(selectedOrders)
            .filter(([_, selected]) => !!selected)
            .map(([key]) => ordersByKey[key]);
    }, [orders, selectedOrders]);

    const onConfirm = () => {
        const routedOrders = ordersToReschedule.filter((order) =>
            order.crossdock_leg === 'pickup' ? !!order.pickup_route_id : !!order.dropoff_route_id
        );
        if (routedOrders.length > 0) {
            setNotification({
                severity: 'warning',
                message: `Cannot reschedule orders that are assigned to a route. Please remove them from the route first. Orders: ${routedOrders
                    .map((o) => o.order_number)
                    .join(', ')}`,
            });
            return onClose();
        }

        const pickups = ordersToReschedule.filter((o) => o.crossdock_leg === 'pickup');
        const dropoffs = ordersToReschedule.filter((o) => o.crossdock_leg !== 'pickup');

        const newDateISO = rescheduleDate ? asUTCDate(rescheduleDate).toISOString() : null;
        const pickupUpdate = newDateISO
            ? {
                  pickup_date: newDateISO,
                  ...reschedulePatch,
              }
            : {
                  pickup_date: null,
                  pickup_planning: false,
                  ...reschedulePatch,
              };
        // Moving the delivery day re-books the resource, so the order releases it either way.
        const dropoffUpdate = newDateISO
            ? {
                  delivery_date: newDateISO,
                  resource_id: null,
                  ...reschedulePatch,
              }
            : {
                  delivery_date: null,
                  planning: false,
                  resource_id: null,
                  ...reschedulePatch,
              };

        const stayPickup = matchesOrderCondition({ pickup_date: newDateISO }, variables.pickupCondition);
        const stayDropoff = matchesOrderCondition({ delivery_date: newDateISO }, variables.dropoffCondition);
        const pickupIds = new Set(pickups.map((o) => o.order_id));
        const dropoffIds = new Set(dropoffs.map((o) => o.order_id));

        updateOrders({
            variables: {
                updates: [
                    ...(pickups.length > 0
                        ? [
                              {
                                  where: { order_id: { _in: pickups.map((o) => o.order_id) } },
                                  _set: pickupUpdate,
                              },
                          ]
                        : []),
                    ...(dropoffs.length > 0
                        ? [
                              {
                                  where: { order_id: { _in: dropoffs.map((o) => o.order_id) } },
                                  _set: dropoffUpdate,
                              },
                          ]
                        : []),
                ],
            },
            // The returning already refreshes order scalars; reschedule only
            // ever changes bucket MEMBERSHIP, so drop rows whose new leg date
            // no longer satisfies the query's window.
            update: (cache) => {
                if (stayPickup && stayDropoff) return;
                cache.updateQuery({ query: QUERY_PLANNING_DATA, variables }, (prev) =>
                    prev
                        ? {
                              ...prev,
                              pickups: stayPickup
                                  ? prev.pickups
                                  : (prev.pickups || []).filter((o) => !pickupIds.has(o.order_id)),
                              dropoffs: stayDropoff
                                  ? prev.dropoffs
                                  : (prev.dropoffs || []).filter((o) => !dropoffIds.has(o.order_id)),
                          }
                        : undefined
                );
            },
            onCompleted: () => {
                setNotification({
                    severity: 'success',
                    message: `Orders rescheduled to ${rescheduleDate}!`,
                });
                setSelectedOrders({});
                onClose();
            },
            onError: (error) => {
                setError(error, 'Error rescheduling orders. Please refresh the page and try again.');
            },
        });
    };

    return (
        <NavResponsiveModal open={modalOpen === MODALS.ORDER_RESCHEDULE} onClose={onClose}>
            <ModalTitle>{`Reschedule Orders`}</ModalTitle>
            <ModalContent>
                <GridItemRow>
                    <Body2>Select a new delivery date for these orders</Body2>
                </GridItemRow>
                <GridItemRow>
                    <TextField
                        type="date"
                        variant="outlined"
                        disabled={loading}
                        value={rescheduleDate}
                        onChange={(e) => setRescheduleDate(e.target.value)}
                    />
                </GridItemRow>
                <GridItemRow>
                    <RescheduleReasonFields {...fieldProps} disabled={loading} />
                </GridItemRow>
            </ModalContent>
            <ModalActions>
                <SecondaryButton disabled={loading} onClick={onClose}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton disabled={loading || !isValid} onClick={onConfirm}>
                    Confirm
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default OrdersReschedule;
